import { faLockKeyhole, faSwapArrows } from '@fortawesome/pro-duotone-svg-icons'
import { Avatar, Card, Stack } from '@mui/material'
import { memo } from 'react'

import { LABEL_TEXT } from '../../constants/messages'
import { MIN_USD_FOR_EXCHANGE } from '../../constants/pointsExchange'
import { EXCHANGE_PROVIDER_PROFILE_DISPATCH } from '../../store/reducers/exchange/exchangeProviders'
import { formatCurrencyValue } from '../../util/number.helpers'
import {
  buildDropdownLabel,
  buildDropdownListData,
  calculateMinMaxPoints,
  checkExchangeGating,
  floorCalculator,
  formatCryptoValue,
  selectExchangeRate
} from '../../util/pointsExchange.helpers'
import { formatTicks } from '../../util/string.helpers'
import { isValidPositiveNumber } from '../../util/validation/commonValidation.helpers'
import { Label } from '../shared/label'
import SubmitButton from '../shared/button/SubmitButton'
import ExchangeProviderIcon from '../shared/exchangeProviderCard/ExchangeProviderIcon'
import FaIcon from 'src/components/shared/faIcon/FaIcon'
import CustomAutoComplete from '../shared/inputs/CustomAutoComplete'
import CustomForm from '../shared/inputs/CustomForm'
import CustomTextField from '../shared/inputs/CustomTextField'
import Text from '../shared/text/Text'
import ExchangeAlert from './ExchangeAlert'

const ExchangeTargetCard = ({
  provider,
  symbol,
  cryptoExchanges,
  t,
  onSubmit,
  dispatchAction,
  exchangeProviderProfile,
  disabled,
  setProvider,
  exchangeWhitelist,
  onAlertClose,
  redeemState,
  loading,
  isKycNotSuccess,
  userTierMetadata
}) => {
  const { pointsAmount, error } = exchangeProviderProfile
  const { exchangeRate, isCrypto, maxUsdExchangeLimit } = provider || {}

  const offramp = provider?.exchangeOfframp || 0
  const checkValidPositiveNumber = isValidPositiveNumber(pointsAmount)
  const { minPoints, maxPoints } = calculateMinMaxPoints(
    { maxUsdExchangeLimit, minUsdExchangeLimit: MIN_USD_FOR_EXCHANGE },
    offramp
  )
  const maxLength = String(maxPoints).length
  const targetProviderSymbol = provider?.symbol || ''
  const exchangeValue = checkValidPositiveNumber
    ? isCrypto
      ? formatCryptoValue(
          pointsAmount,
          offramp,
          selectExchangeRate(cryptoExchanges, provider)
        )
      : floorCalculator(pointsAmount * exchangeRate)
    : 0
  const providerId = provider?.tokenId || ''
  return (
    <Card sx={{ p: 4, width: 1, height: 'fit-content' }}>
      <CustomForm onFormSubmit={onSubmit}>
        <Stack spacing={4}>
          <Stack spacing={1.5}>
            <Text
              label={`${LABEL_TEXT.MIN}. ${minPoints} ${symbol} ${maxPoints ? `- ${LABEL_TEXT.MAX}. ${maxPoints} ${symbol}` : ''}`}
            />
            <CustomTextField
              type='number'
              label={t('exchange.points-amount', { symbol })}
              value={pointsAmount}
              maxLength={maxLength}
              error={t(error[EXCHANGE_PROVIDER_PROFILE_DISPATCH.POINTS_AMOUNT])}
              positiveNumber
              onChange={(value) =>
                dispatchAction(
                  EXCHANGE_PROVIDER_PROFILE_DISPATCH.POINTS_AMOUNT,
                  value
                )
              }
              help={
                !isCrypto && targetProviderSymbol
                  ? t('exchange.exchange-rate', {
                      brandPoints: `1 ${symbol}`,
                      targetBrandPoints: `${formatCurrencyValue(exchangeRate, 3)} ${targetProviderSymbol}`
                    })
                  : ''
              }
            />

            <Avatar
              sx={{
                width: 48,
                height: 48,
                color: 'primary.main',
                bgcolor: 'background.neutral',
                mx: 'auto'
              }}
            >
              <FaIcon icon={faSwapArrows} size='lg' />
            </Avatar>
            <CustomAutoComplete
              id='exchange-target-select'
              label={t('shop.exchange-to')}
              placeholder={t('shop.select-option')}
              options={buildDropdownListData(exchangeWhitelist)}
              value={
                providerId
                  ? { value: providerId, label: buildDropdownLabel(provider) }
                  : {}
              }
              onChange={(value) => {
                if (value) {
                  const provider = exchangeWhitelist.find(
                    (option) => option.tokenId === value
                  )
                  if (provider) {
                    setProvider(provider)
                  }
                } else {
                  setProvider(null)
                }
              }}
              startIcon={
                provider && (
                  <ExchangeProviderIcon
                    provider={provider}
                    iconSize={24}
                    fontSize={15}
                    noBorder
                  />
                )
              }
              renderOption={(props, option) => {
                const isDisabled =
                  isKycNotSuccess ||
                  !checkExchangeGating(option, userTierMetadata)
                return (
                  <li
                    {...props}
                    key={option.value}
                    style={isDisabled ? { opacity: 0.5 } : {}}
                  >
                    {option.symbol && (
                      <ExchangeProviderIcon
                        provider={option}
                        iconSize={24}
                        fontSize={15}
                        noBorder
                        sx={{ mr: 1 }}
                      />
                    )}
                    {option.label}
                    {isDisabled && (
                      <Label
                        color='default'
                        endIcon={faLockKeyhole}
                        sx={{ ml: 'auto' }}
                      />
                    )}
                  </li>
                )
              }}
            />
          </Stack>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              color: 'var(--layout-nav-text-primary-color)',
              bgcolor: 'background.neutral',
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Text variant='h4' label={formatTicks(exchangeValue)} />
            {provider && (
              <Stack direction='row'>
                <ExchangeProviderIcon
                  provider={provider}
                  iconSize={24}
                  fontSize={15}
                  noBorder
                  sx={{ mr: 1 }}
                />
                <Text variant='subtitle2' label={targetProviderSymbol} />
              </Stack>
            )}
          </Stack>
          <Stack alignItems='end'>
            <SubmitButton
              disabled={disabled || loading || exchangeValue <= 0}
              label={t('navigation.exchange', { symbol })}
              loading={loading}
            />
          </Stack>
          {redeemState && (
            <ExchangeAlert
              t={t}
              redeemState={redeemState}
              onClose={onAlertClose}
              symbol={symbol}
              offramp={offramp}
              targetSymbol={targetProviderSymbol}
              provider={provider}
              reward={{ value: exchangeProviderProfile.pointsAmount }}
            />
          )}
        </Stack>
      </CustomForm>
    </Card>
  )
}

export default memo(ExchangeTargetCard)
